import * as riot from "riot";
import { PublicClientApplication } from "@azure/msal-browser";
import events from "./events.js";

// --- MSAL Configuration ---
const msalConfig = {
  auth: {
    clientId: "38927e6a-2ec1-4efc-9b3a-592e5ba195cc",
    authority: "https://edjro.ciamlogin.com/14ce2c53-06c6-40f3-8532-2f4ede74854a",
    redirectUri: (() => {
      const { protocol, hostname, port } = window.location;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${protocol}//${hostname}${port ? ':' + port : ''}`;
      } else if (hostname === 'filament.edjro.com') {
        return 'https://filament.edjro.com';
      } else {
        return `${protocol}//${hostname}`;
      }
    })(),
    postLogoutRedirectUri: (() => {
      const { protocol, hostname, port } = window.location;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${protocol}//${hostname}${port ? ':' + port : ''}`;
      } else if (hostname === 'filament.edjro.com') {
        return 'https://filament.edjro.com';
      } else {
        return `${protocol}//${hostname}`;
      }
    })(),
    // Process the redirect response on the redirect URI page itself instead of
    // navigating back to the login-request URL first. The extra navigation
    // drops the auth response hash in this hash-routed (riot-route) SPA, which
    // caused handleRedirectPromise() to return null and the "must click login
    // twice" behavior.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

const loginRequest = {
  scopes: ["api://7f5afec6-9ac1-4b7d-b5ac-11635b335553/access"],
};

export const FILAMENT_API_ENDPOINT =
  "https://smartkits.azure-api.net/filament";

// Keep old export name for backward compatibility during migration
export const SMARTKITS_API_ENDPOINT = FILAMENT_API_ENDPOINT;

const msalInstance = new PublicClientApplication(msalConfig);
export { msalInstance };

/**
 * Initialize MSAL — must be called once on page load.
 * Handles redirect promise and emits auth events.
 */
export async function initializeMsal() {
  try {
    await msalInstance.initialize();
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      // User just returned from login redirect
      msalInstance.setActiveAccount(response.account);
      // Sync token to localStorage for legacy tag code
      localStorage.setItem('accessToken', response.accessToken);
      const name = response.account.name || '';
      // Use idTokenClaims.email (real identity email e.g. Google) over username (Entra UPN)
      const claims = response.account.idTokenClaims || {};
      const email = claims.email || (claims.emails && claims.emails[0]) || response.account.username;
      localStorage.setItem('profile', JSON.stringify({
        first_name: name.split(' ')[0] || '',
        last_name: name.split(' ').slice(1).join(' ') || '',
        email: email,
        picture: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=0e0e52&color=fff&size=64`,
        user_id: claims.oid || response.account.localAccountId,
      }));
      events.emit("auth_complete");
    } else {
      // Check if user is already signed in (returning user, no redirect)
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
        // Pre-fetch token for legacy tag code
        await getAccessToken();
        // Ensure profile user_id is always synced to the token's oid
        const account = accounts[0];
        const claims = account.idTokenClaims || {};
        const correctUserId = claims.oid || account.localAccountId;
        const existingProfile = localStorage.getItem('profile');
        let needsUpdate = !existingProfile || existingProfile === 'undefined';

        if (!needsUpdate) {
          try {
            const parsed = JSON.parse(existingProfile);
            if (parsed.user_id !== correctUserId) needsUpdate = true;
          } catch { needsUpdate = true; }
        }

        if (needsUpdate) {
          const name = account.name || '';
          const email = claims.email || (claims.emails && claims.emails[0]) || account.username;
          localStorage.setItem('profile', JSON.stringify({
            first_name: name.split(' ')[0] || '',
            last_name: name.split(' ').slice(1).join(' ') || '',
            email: email,
            picture: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=0e0e52&color=fff&size=64`,
            user_id: correctUserId,
          }));
        }
      }
    }
  } catch (err) {
    console.error("MSAL initialization error:", err);
    showLoginError(
      "Sign-in didn't complete. Please try again.",
    );
  }
}

/**
 * Show or hide the sign-in error banner on the welcome screen.
 * @param {string|null} message - Message to display, or null to hide the banner.
 */
function showLoginError(message) {
  const el = document.getElementById("welcome-login-error");
  if (!el) return;
  if (message) {
    el.textContent = message;
    el.style.display = "";
  } else {
    el.style.display = "none";
  }
}

/**
 * Login via redirect
 */
export function login() {
  showLoginError(null);
  msalInstance.loginRedirect({
    ...loginRequest,
    prompt: "select_account",
  });
}

/**
 * Logout via redirect
 */
export function logout() {
  const account = msalInstance.getActiveAccount();
  localStorage.removeItem('profile');
  localStorage.removeItem('accessToken');
  msalInstance.logoutRedirect({
    account: account,
  });
}

/**
 * Get access token silently (MSAL handles caching and refresh automatically)
 * Also syncs to localStorage for legacy tag code that reads it directly.
 * @returns {Promise<string|null>} The access token or null
 */
export async function getAccessToken() {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    localStorage.removeItem('accessToken');
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account,
    });
    // Sync to localStorage for legacy tag code
    localStorage.setItem('accessToken', response.accessToken);
    return response.accessToken;
  } catch (err) {
    console.error("Silent token acquisition failed:", err);
    clearLocalSession();
    events.emit("auth_logout", { reason: "token_renewal_failed" });
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return msalInstance.getActiveAccount() !== null;
}

// Token renewal is handled automatically by MSAL's acquireTokenSilent.
// Export no-op functions to avoid breaking existing code that calls them.
export function startTokenRenewal() {}
export function stopTokenRenewal() {}
export function isTokenExpired() {
  return !isAuthenticated();
}

/**
 * Clear local session state (localStorage + MSAL active account).
 * Does NOT trigger a server-side logout redirect.
 * Only removes accounts — preserves interaction state so next login works.
 */
function clearLocalSession() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('profile');
  msalInstance.setActiveAccount(null);
  // clearCache() removes accounts + tokens from the MSAL cache but preserves
  // interaction/PKCE state needed for the next login redirect.
  try {
    msalInstance.clearCache();
  } catch (err) {
    console.warn("clearCache failed:", err);
  }
}

/**
 * Makes an authenticated HTTP request to the SmartKits API.
 *
 * Automatically attaches the `Authorization` header using the access token stored
 * in localStorage. Handles 401 errors by clearing local cache and notifying the app.
 *
 * @param {string} endpoint - The relative endpoint to call (e.g. "GetActivities").
 * @param {Object} [options] - Optional fetch settings.
 * @param {string} [options.method="GET"] - HTTP method (e.g. "GET", "POST", "PUT").
 * @param {Object|null} [options.body=null] - Request payload for methods like POST or PUT.
 * @param {Object} [options.headers={}] - Additional headers to merge with defaults.
 *
 * @returns {Promise<Object>} The parsed JSON response from the API.
 *
 * @throws {Error} If the response is not OK (status not in 200â€“299 range).
 */
export async function skApiRequest(
  endpoint,
  options = { method: "GET", body: null, headers: {} },
) {
  const url = `${FILAMENT_API_ENDPOINT}/${endpoint}`;
  const token = await getAccessToken();
  if (!token) {
    // No token — either no active account or silent renewal failed.
    // Ensure the welcome screen is shown even if getAccessToken didn't emit
    // (e.g. session was already cleared on a prior call).
    clearLocalSession();
    events.emit("auth_logout", { reason: "not_authenticated" });
    throw new Error("Not authenticated");
  }
  options.headers = new Headers({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...options.headers,
  });
  if (options.body) {
    options.body = JSON.stringify(options.body);
  }
  const response = await fetch(url, options);
  if (response.status === 401) {
    clearLocalSession();
    events.emit("auth_logout", { reason: "token_expired" });
    throw new Error("Session expired");
  }
  if (!response.ok) {
    throw new Error(response.statusText);
  }
  return response.json();
}

export async function putUser(data) {
  const res = await skApiRequest("PUTUser", { method: "PUT", body: data });
  const user = res?.data?.users?.[0];
  return user;
}

export async function getLessons() {
  return await skApiRequest("GetLessons", { method: "GET" });
}

export async function putActivities(data) {
  return await skApiRequest("PUTActivity", { method: "PUT", body: data });
}

export async function postActivities(data) {
  return await skApiRequest("POSTActivity", { method: "POST", body: data });
}

export async function getActivities() {
  return await skApiRequest("GetActivities", { method: "GET" });
}

export async function postLesson(data) {
  return await skApiRequest("POSTLesson", { method: "POST", body: data });
}

export async function putLesson(data) {
  return await skApiRequest("PUTLesson", { method: "PUT", body: data });
}

export async function postQuestion(data) {
  return await skApiRequest("POSTQuestion", { method: "POST", body: data });
}

export async function getQuestions(lesson_id) {
  return await skApiRequest(`GETQuestions?lessonId=${lesson_id}`, {
    method: "GET",
  });
}

export async function getAnswers(activity_ref, user_id, question_id) {
  let url = `GETAnswers?activity_ref=${activity_ref}&user_id=${user_id}`;
  if (question_id) {
    url += `&questionId=${question_id}`;
  }
  return await skApiRequest(url, { method: "GET" });
}

export async function postAnswers(data) {
  return await skApiRequest("POSTAnswers", {
    method: "POST",
    body: { answer: data },
  });
}

export async function getResponses(activity_ref) {
  return await skApiRequest(`GETResponses?activity_ref=${activity_ref}`, { method: "GET" });
}

export async function postResponses(data) {
  return await skApiRequest("POSTResponses", {
    method: "POST",
    body: data,
  });
}

export async function postDevices(data) {
  return await skApiRequest("POSTSmartkit", { method: "POST", body: data });
}

export async function getDeviceTelemetry(device) {
  return await skApiRequest(`GETTelemetry?device=${device}`, { method: "GET" });
}

export async function getDeviceMonitors(device) {
  return await skApiRequest(`GETMonitors?device=${device}`, { method: "GET" });
}

export async function putDevice(data) {
  return await skApiRequest("PUTSmartkit", { method: "PUT", body: data });
}

export async function getActivityNotes(activity_ref) {
  return await skApiRequest(`GETNotes?activity_ref=${activity_ref}`, {
    method: "GET",
  });
}

export async function postActivityNotes(note) {
  return await skApiRequest("POSTNotes", { method: "POST", body: { note } });
}

export async function getActivityDetails(activity_ref) {
  return await skApiRequest(`GETActivityDetails?activity_ref=${activity_ref}`, {
    method: "GET",
  });
}

export async function getActivityMonitors(activity_ref) {
  return await skApiRequest(`GETMonitors?activity_ref=${activity_ref}`, {
    method: "GET",
  });
}

export async function getActivityTelemetry(device, activity_ref) {
  return await skApiRequest(
    `GETTelemetry?device=${device}&activity_ref=${activity_ref}`,
    { method: "GET" },
  );
}

export async function deleteActivityTelemetry(id, activity_ref) {
  return await skApiRequest("DELETETelemetry", {
    method: "DELETE",
    body: { id, activity_ref },
  });
}

export async function postDirectMethod(data) {
  return await skApiRequest("POSTDirectMethod", { method: "POST", body: data });
}

export async function getMonitors(activity_ref, device) {
  return await skApiRequest(
    `GETMonitors?activity_ref=${activity_ref}&device=${device}`,
    { method: "GET" },
  );
}

export async function postMonitor(data) {
  return await skApiRequest("POSTMonitor", {
    method: "POST",
    body: { monitor: data },
  });
}

export async function deleteMonitor(data) {
  return await skApiRequest("DELETEMonitor", { method: "DELETE", body: data });
}

export async function getSmartkits(device) {
  let endpoint = "GetSmartkits";
  if (device) {
    endpoint += `?device=${device}`;
  }
  return await skApiRequest(endpoint, { method: "GET" });
}

export async function putProgress(data) {
  const user = await putUser(data);
  if (user) {
    localStorage.setItem("profile", JSON.stringify(user));
    events.emit("progress_updated");
  }
  return user;
}

export async function getProjects() {
  return await skApiRequest("GETProjects", { method: "GET" });
}

export async function getSensors() {
  return await skApiRequest("GetSensors", { method: "GET" });
}

/**
 * Ensure the sensors list is cached in localStorage so synchronous lookups
 * (getSensorDisplayName / getSupplyDisplayName) work without a per-component fetch.
 * Safe to call multiple times; only fetches when the cache is empty.
 * Emits "sensors_ready" once the cache is populated (or already present).
 */
export async function ensureSensorsCached() {
  const cached = localStorage.getItem("sensors");
  if (cached) {
    events.emit("sensors_ready");
    return;
  }
  try {
    const res = await getSensors();
    if (res && res.data && Array.isArray(res.data.sensors)) {
      localStorage.setItem("sensors", JSON.stringify(res.data.sensors));
    }
  } catch (err) {
    console.error("Failed to pre-warm sensors cache:", err);
  } finally {
    events.emit("sensors_ready");
  }
}

/**
 * Synchronously report whether the sensor list has been cached yet.
 * Components can use this to show a "loading" state for supply names.
 * @returns {boolean}
 */
export function areSensorsLoaded() {
  return !!localStorage.getItem("sensors");
}

/**
 * Synchronously resolve a sensor's user-friendly display name from the cached
 * sensor list. Falls back to the identifier if the cache is missing or the
 * sensor isn't found.
 * @param {string} sensorName - Exact sensor identifier (e.g. "soil_moisture")
 * @returns {string}
 */
export function getSensorDisplayName(sensorName) {
  if (!sensorName) return sensorName;
  const cached = localStorage.getItem("sensors");
  if (cached) {
    try {
      const sensors = JSON.parse(cached);
      const found = sensors.find((s) => s.name === sensorName);
      if (found && found.display_name) return found.display_name;
    } catch (e) {
      console.error("Failed to parse cached sensors:", e);
    }
  }
  return sensorName;
}

/**
 * Resolve the name to display for a lesson/project supply. Sensor supplies
 * (type === "sensor") are translated to their friendly display name; all other
 * supplies use their raw name.
 * @param {{ name: string, type?: string }} supply
 * @returns {string}
 */
export function getSupplyDisplayName(supply) {
  if (!supply) return "";
  return supply.type === "sensor"
    ? getSensorDisplayName(supply.name)
    : supply.name;
}

export function getUser() {
  // TODO: this should be called load user or load local profile instead as current name is misleading
  user_profile = localStorage.getItem("profile");
  if (user_profile != "undefined") {
    return JSON.parse(user_profile);
  }
  return null;
}

/**
 * Global helper to open sensor/component setup documentation
 * @param {string} name - Exact sensor name (from sensor database)
 */
export function openSensorSetupGuide(name) {
  if (!name) return;
  
  const sensorName = name.toLowerCase();
  
  switch (sensorName) {
    case 'soil_moisture':
      window.open('support/soil_moisture.html', '_blank');
      break;
    case 'dht11':
      window.open('support/dht11.html', '_blank');
      break;
    case 'hall':
      window.open('support/hall.html', '_blank');
      break;
    case 'photoresistor':
      window.open('support/photoresistor.html', '_blank');
      break;
    case 'lm358':
      window.open('support/lm358.html', '_blank');
      break;
    case 'mq2':
      window.open('support/mq2.html', '_blank');
      break;
    case 'ir_object_detection':
      window.open('support/ir_object_detection.html', '_blank');
      break;
    case 'hcsr04':
      window.open('support/hcsr04.html', '_blank');
      break;
    case 'flame':
      window.open('support/flame.html', '_blank');
      break;
    case 'buzzer':
      window.open('support/passive_buzzer.html', '_blank');
      break;
    case 'passive_buzzer':
      window.open('support/passive_buzzer.html', '_blank');
      break;
    case 'dual_color_led':
      window.open('support/dual_color_led.html', '_blank');
      break;
    case 'vibration':
      window.open('support/vibration.html', '_blank');
      break;
    case 'filament':
      window.open('support/filament_setup.html', '_blank');
      break;
    default:
      alert("Setup information for " + name + " coming soon! Please check the support section for available guides.");
  }
}

document.addEventListener("DOMContentLoaded", async () => {
  // Register auth_logout handler FIRST so it's ready before any component emits it
  events.on("auth_logout", () => {
    document.getElementById("app-content").style.display = "none";
    const welcomeScreen = document.getElementById("welcome-screen");
    if (welcomeScreen) {
      welcomeScreen.style.display = "";
    }
  });

  // Always attach login button handler (needed after session-timeout logout too)
  document.getElementById("welcome-login-btn").addEventListener("click", () => {
    login();
  });

  await initializeMsal();
  riot.mount("main-nav");

  if (isAuthenticated()) {
    // User is logged in — show the app
    document.getElementById("app-content").style.display = "";
    // Pre-warm the sensors cache so supply display names resolve synchronously
    // (avoids the raw sensor identifier flashing before the friendly name loads)
    ensureSensorsCached();
    riot.mount("smartkits-tabs");
  } else {
    // No active account — clear any stale profile/token from a previous session
    // so the nav doesn't show a phantom logged-in user.
    clearLocalSession();
    events.emit("auth_logout", { reason: "no_active_account" });
    // Not logged in — show welcome screen with login button
    const welcomeScreen = document.getElementById("welcome-screen");
    welcomeScreen.style.display = "";
  }
});
